/**
 * @voltxampere/web — Thermal frame producer (Sprint 34-4)
 *
 * Bridges the engine result signals (DC + transient) to the
 * `@voltxampere/thermal` package's `ThermalFrame` shape.
 *
 * Engine emits per-NET voltages + per-V-source/inductor currents
 * only — see ADR-0055 proposal (per-terminal current emission for
 * BJT/MOSFET deferred to Aşama 3 S38+). The producer here derives
 * per-component (V, I) using:
 *   - V = nodeVoltages[pin_a.netId] - nodeVoltages[pin_b.netId]
 *   - I:
 *       * Voltage source / inductor: branchCurrents/sourceCurrents map
 *       * Resistor: V / R (analytical from `params.resistance`)
 *       * Other (LED, diode, BJT, MOSFET, cap): I=0 fallback for
 *         Sprint 34. Inspector + canvas overlay show "no thermal data
 *         until current available" — Sprint 34 prompt §"Out of Scope"
 *         + ADR-0055 future work.
 *
 * Time evolution skipped — Sprint 37+ adds RC thermal network.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, type ReadonlySignal } from "@preact/signals-core";
import {
  computeThermalFrame,
  type ThermalFrame,
  type ThermalMeasurement,
} from "@voltxampere/thermal";
import type { Circuit, ComponentInstance } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";
import { currentDCResult } from "./dc-result.js";
import { currentTransientResult } from "./transient-runner.js";

interface PerComponentVI {
  readonly V: number;
  readonly I: number;
}

/**
 * Resolve per-component (V, I) from engine maps. Pure function for
 * test isolation; the signal-bound `currentThermalFrame` below
 * orchestrates the pipeline.
 *
 * Returns null when V cannot be derived (component lacks 2 terminals
 * or pin nets are missing from `nodeVoltages`).
 */
export function deriveComponentVI(
  component: ComponentInstance,
  nodeVoltages: Record<string, number> | ReadonlyMap<string, number>,
  branchCurrents: Record<string, number> | ReadonlyMap<string, number>,
): PerComponentVI | null {
  if (component.terminals.length < 2) return null;
  const a = component.terminals[0];
  const b = component.terminals[1];
  if (a === undefined || b === undefined) return null;

  const va = readMap(nodeVoltages, a.netId);
  const vb = readMap(nodeVoltages, b.netId);
  if (va === undefined || vb === undefined) return null;
  const V = va - vb;

  // I derivation — type-specific
  let I = 0;

  // Voltage source / inductor: engine emits branch current directly
  const ib = readMap(branchCurrents, component.id);
  if (ib !== undefined && Number.isFinite(ib)) {
    I = ib;
  } else if (component.type === "resistor") {
    const r = readResistance(component);
    if (r !== null && r !== 0) {
      I = V / r;
    }
  }
  // LED / diode / BJT / MOSFET / cap: I stays 0 in Sprint 34.
  // ADR-0055 future work — engine per-terminal current emission.

  return { V, I };
}

function readMap(
  source: Record<string, number> | ReadonlyMap<string, number>,
  key: string,
): number | undefined {
  if (isReadonlyMap(source)) return source.get(key);
  if (!Object.prototype.hasOwnProperty.call(source, key)) return undefined;
  const value: unknown = source[key];
  return typeof value === "number" ? value : undefined;
}

function isReadonlyMap(
  value: Record<string, number> | ReadonlyMap<string, number>,
): value is ReadonlyMap<string, number> {
  return value instanceof Map;
}

function readResistance(component: ComponentInstance): number | null {
  const r: unknown = component.params.resistance;
  if (typeof r === "number" && Number.isFinite(r)) return r;
  return null;
}

/**
 * Build measurements array from a circuit + voltage/current maps.
 * Skips components whose V cannot be derived.
 */
export function buildThermalMeasurements(
  circuit: Circuit,
  nodeVoltages: Record<string, number> | ReadonlyMap<string, number>,
  branchCurrents: Record<string, number> | ReadonlyMap<string, number>,
): ThermalMeasurement[] {
  const out: ThermalMeasurement[] = [];
  for (const c of circuit.components) {
    const vi = deriveComponentVI(c, nodeVoltages, branchCurrents);
    if (vi === null) continue;
    const variant = readVariant(c);
    out.push({
      id: c.id,
      type: c.type,
      ...(variant !== undefined ? { variant } : {}),
      voltage: vi.V,
      current: vi.I,
    });
  }
  return out;
}

function readVariant(component: ComponentInstance): string | undefined {
  const v: unknown = component.params.variant;
  if (typeof v === "string" && v.length > 0) return v;
  return undefined;
}

/**
 * Reactive thermal frame derived from the latest engine result.
 *
 * Priority: transient (last sample) → DC → null (no thermal data).
 * The latest transient sample reflects the most up-to-date
 * operating point (always-live mode keeps it fresh per Sprint 33-1).
 */
export const currentThermalFrame: ReadonlySignal<ThermalFrame | null> = computed(() => {
  const circuit = currentCircuit.value;
  if (circuit === null) return null;

  const transient = currentTransientResult.value;
  if (transient !== null && transient.timeSeries.length > 0) {
    const last = transient.timeSeries[transient.timeSeries.length - 1];
    if (last !== undefined) {
      const measurements = buildThermalMeasurements(
        circuit,
        last.nodeVoltages,
        last.sourceCurrents,
      );
      return computeThermalFrame({ t: last.t, measurements });
    }
  }

  const dc = currentDCResult.value;
  if (dc !== null) {
    const measurements = buildThermalMeasurements(circuit, dc.nodeVoltages, dc.branchCurrents);
    return computeThermalFrame({ t: 0, measurements });
  }

  return null;
});
